import {geofenceSiteGroupsSelector, useGetAllGeofenceSiteGroupsQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {
  GeofenceSiteGroupDataTable
} from "../../components/data-table/GeofenceSiteGroupDataTable/GeofenceSiteGroupDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useNavigate} from "react-router-dom";
import { useApp } from "../../providers/RegionProvider/RegionProvider";

export type GeofenceSiteGroupDataTablePageProps = {}

export const GeofenceSiteGroupDataTablePageRoute = () => {
  return (
    <GeofenceSiteGroupDataTablePage/>
  );
};

export const GeofenceSiteGroupDataTablePage = (props: GeofenceSiteGroupDataTablePageProps) => {

  const {regionId} = useApp()
  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectGeofenceSiteGroups = useMemo(() => {
    return geofenceSiteGroupsSelector();
  }, []);

  const {
    geofenceSiteGroups,
    isLoading,
    error,
    refetch,
  } = useGetAllGeofenceSiteGroupsQuery(access ? regionId : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      geofenceSiteGroups: selectGeofenceSiteGroups(result, {regionId: regionId}),
      isLoading: result.isLoading,
      error: result.error
    })
  });

  return (
    <ProtectedPage permissions={["view", "geofencesitegroup_view"]} isLoading={isLoading} error={error}
                   onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.geofencesitegroup.plural", "Geofence Site Groups", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["geofencesitegroup_create"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/geofence-site-groups/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <GeofenceSiteGroupDataTable
                  withBorder
                  records={geofenceSiteGroups}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  )
};
